import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import SubjectsService from "services/SubjectsService";

export const getSubjects = createAsyncThunk(
  "subjects/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SubjectsService.getSubjects();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error");
    }
  }
);

export const getSubject = createAsyncThunk(
  "subjects/getOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await SubjectsService.getSubject(id);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error");
    }
  }
);

export const createSubject = createAsyncThunk(
  "subjects/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SubjectsService.createSubject(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error");
    }
  }
);

export const updateSubject = createAsyncThunk(
  "subjects/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await SubjectsService.updateSubject(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error");
    }
  }
);

export const deleteSubject = createAsyncThunk(
  "subjects/delete",
  async (id, { rejectWithValue }) => {
    try {
      await SubjectsService.deleteSubject(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error");
    }
  }
);

const initialState = {
  subjects: [],
  subject: null,
  loading: false,
  error: null,
};

const subjectsSlice = createSlice({
  name: "subjects",
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Get all subjects
      .addCase(getSubjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubjects.fulfilled, (state, { payload }) => {
        state.subjects = payload;
        state.loading = false;
      })
      .addCase(getSubjects.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // Get one subject
      .addCase(getSubject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubject.fulfilled, (state, { payload }) => {
        state.subject = payload;
        state.loading = false;
      })
      .addCase(getSubject.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // Create subject
      .addCase(createSubject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSubject.fulfilled, (state, { payload }) => {
        state.subjects.push(payload);
        state.loading = false;
      })
      .addCase(createSubject.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // Update subject
      .addCase(updateSubject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubject.fulfilled, (state, { payload }) => {
        const index = state.subjects.findIndex(
          (subject) => subject?._id === payload._id
        );
        if (index !== -1) {
          state.subjects[index] = payload;
        }
        state.loading = false;
      })
      .addCase(updateSubject.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // Delete subject
      .addCase(deleteSubject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSubject.fulfilled, (state, { payload }) => {
        state.subjects = state.subjects.filter(
          (subject) => subject?._id !== payload
        );
        state.loading = false;
      })
      .addCase(deleteSubject.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      });
  },
});

export const { resetState } = subjectsSlice.actions;

export default subjectsSlice.reducer;
